import { toChartTimestamp, utcToChartTimestamp } from '../timezone.js'

const intervals = { '1min': 60, '2min': 120, '5min': 300, '15min': 900, '30min': 1800, '1hour': 3600, '65min': 3900 }
const dayFormat = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' })
function dayKey(epoch) { return dayFormat.format(new Date(epoch * 1000)).replaceAll('-', '') }

export function candleAnchorEpoch(candle, timeframe, timezone) {
  if (!candle || !Number.isFinite(candle.time)) return null
  if (timeframe === 'daily' || timeframe === 'weekly') {
    const date = candle.ibkrDayKey || new Date(candle.time * 1000).toISOString().slice(0, 10).replaceAll('-', '')
    return toChartTimestamp(`${date} 09:30:00 US/Eastern`, 'UTC') || null
  }
  if (!Number.isFinite(candle.rawEpochMs)) return null
  const epoch = candle.rawEpochMs / 1000
  // Live bars may carry the latest receipt instant. Recover the candle's
  // opening instant from the chart's display coordinate, without using the
  // browser's timezone or confusing daily UTC-midnight with a session open.
  return epoch + candle.time - utcToChartTimestamp(epoch, timezone)
}

export function projectRetainedVwap(snapshot, candles, timeframe, timezone) {
  const slots = (candles || []).map(candle => ({ candle, start: candleAnchorEpoch(candle, timeframe, timezone) }))
    .filter(x => Number.isFinite(x.start))
  const values = new Map()
  const dailyDisplay = timeframe === 'daily' || timeframe === 'weekly'
  let index = 0
  for (const point of snapshot.points || []) {
    if (!Number.isFinite(point.time) || !Number.isFinite(point.value) || !slots.length) continue
    const cutoff = snapshot.tailTime
    const seconds = intervals[snapshot.resolution]
    const liveTail = point === snapshot.points.at(-1) && Number.isFinite(cutoff) && cutoff >= point.time
      && (snapshot.resolution === 'daily' ? dayKey(cutoff) === dayKey(point.time) : seconds && cutoff < point.time + seconds)
    const displayTime = liveTail ? cutoff : point.time
    // A single forming input still keeps its original anchor coordinate.
    if (liveTail && snapshot.points.length === 1 && !dailyDisplay && snapshot.resolution !== 'daily') {
      const anchorSlot = slots.find(x => x.start <= point.time && point.time < x.start + intervals[timeframe])
      if (anchorSlot) values.set(anchorSlot.candle.time, point.value)
    }
    while (index + 1 < slots.length && slots[index + 1].start <= displayTime) index++
    let slot = slots[index]
    if (displayTime < slot.start) continue
    if (snapshot.resolution === 'daily' && !dailyDisplay && !liveTail) {
      // Historical daily values belong at the last candle of their session.
      const date = dayKey(point.time)
      let last = index
      while (last + 1 < slots.length && dayKey(slots[last + 1].start) === date) last++
      slot = slots[last]
      if (dayKey(slot.start) !== date) continue
    } else if (!dailyDisplay && displayTime >= slot.start + (intervals[timeframe] || 0)) continue
    else if (timeframe === 'daily' && dayKey(displayTime) !== dayKey(slot.start)) continue
    else if (timeframe === 'weekly' && displayTime >= slot.start + 7 * 86400) continue
    values.set(slot.candle.time, point.value)
  }
  // Only existing candle coordinates reach the overlay: no added timeline
  // slots, no OHLC aggregation and no client-side VWAP recalculation.
  return [...values].sort((a, b) => a[0] - b[0]).map(([time, value]) => ({ time, value }))
}

export function applyRetainedSeries(series, previous, next) {
  if (previous.length === next.length && previous.every((p, i) => p.time === next[i].time && p.value === next[i].value)) return
  const prefix = previous.slice(0, -1)
  if (previous.length && next.length >= previous.length && prefix.every((p, i) => p.time === next[i]?.time && p.value === next[i]?.value)
      && previous.at(-1).time === next[previous.length - 1]?.time) {
    for (const p of next.slice(previous.length - 1)) series.update(p)
  } else series.setData(next)
}

// Hit-test rendered straight segments in screen coordinates. This also works
// between samples on finer chart intervals and on logarithmic price scales.
export function hitTestRetainedVwap(entries, timeScale, x, y, tolerance = 8) {
  let nearest = null, distance = tolerance
  for (const [id, entry] of entries) {
    if (entry.hidden || !entry.points.length) continue
    const points = entry.points
    let low = 0, high = points.length
    while (low < high) {
      const mid = (low + high) >>> 1
      const px = timeScale.timeToCoordinate(points[mid].time)
      if (px !== null && px < x) low = mid + 1
      else high = mid
    }
    const a = points[Math.max(0, low - 1)], b = points[Math.min(low, points.length - 1)]
    const ax = timeScale.timeToCoordinate(a.time), bx = timeScale.timeToCoordinate(b.time)
    const ay = entry.series.priceToCoordinate(a.value), by = entry.series.priceToCoordinate(b.value)
    if ([ax, bx, ay, by].some(v => v === null || !Number.isFinite(v))) continue
    const dx = bx - ax, dy = by - ay
    const ratio = dx || dy ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / (dx * dx + dy * dy))) : 0
    const d = Math.hypot(x - ax - ratio * dx, y - ay - ratio * dy)
    if (d <= distance) { distance = d; nearest = id }
  }
  return nearest
}
